import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/useToast'
import { extractApiError } from '@/lib/api/client'
import { printApi, resolveAgentId } from '@/lib/api/print'
import { tasksApi } from '@/lib/api/tasks'

const POLL_MS = 1500
const TIMEOUT_MS = 120_000

async function waitForBoxPrinted(taskUuid: string, boxNum: number, signal: AbortSignal) {
  const started = Date.now()
  while (Date.now() - started < TIMEOUT_MS) {
    if (signal.aborted) throw new Error('Отменено')
    const res = await tasksApi.getBoxes(taskUuid, { limit: 1, offset: 0, q: String(boxNum) })
    const box = res.data.boxes[0]
    if (box?.printed) return
    await new Promise(r => setTimeout(r, POLL_MS))
  }
  throw new Error('Печать не завершилась за отведённое время')
}

export function usePrintBoxWithWait(taskUuid: string) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [isWaiting, setIsWaiting] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['task-boxes', taskUuid] })
    void qc.invalidateQueries({ queryKey: ['task-pallets', taskUuid] })
    void qc.invalidateQueries({ queryKey: ['task-km-codes', taskUuid] })
  }, [qc, taskUuid])

  const printNextBox = useCallback(
    async (boxNum: number) => {
      if (isWaiting) return
      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac
      setIsWaiting(true)
      try {
        const agentId = await resolveAgentId({ localOnly: true })
        await printApi.printBoxBundle(taskUuid, boxNum, { agent_id: agentId })
        await waitForBoxPrinted(taskUuid, boxNum, ac.signal)
        toast.success(`Коробка №${boxNum} напечатана`)
        invalidate()
      } catch (err) {
        toast.error(extractApiError(err))
        throw err
      } finally {
        setIsWaiting(false)
        abortRef.current = null
      }
    },
    [taskUuid, isWaiting, toast, invalidate],
  )

  const printPalletKa = useCallback(
    async (palletNum: number) => {
      if (isWaiting) return
      setIsWaiting(true)
      try {
        const agentId = await resolveAgentId({ localOnly: true })
        await printApi.printPallet(taskUuid, palletNum, { agent_id: agentId })
        toast.success('Задание на печать КА паллеты отправлено')
        invalidate()
      } catch (err) {
        toast.error(extractApiError(err))
        throw err
      } finally {
        setIsWaiting(false)
      }
    },
    [taskUuid, isWaiting, toast, invalidate],
  )

  const printBoxKa = useCallback(
    async (boxNum: number) => {
      if (isWaiting) return
      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac
      setIsWaiting(true)
      try {
        const agentId = await resolveAgentId({ localOnly: true })
        await printApi.printBox(taskUuid, boxNum, { agent_id: agentId })
        await waitForBoxPrinted(taskUuid, boxNum, ac.signal)
        toast.success(`КА коробки №${boxNum} напечатана`)
        invalidate()
      } catch (err) {
        toast.error(extractApiError(err))
        throw err
      } finally {
        setIsWaiting(false)
        abortRef.current = null
      }
    },
    [taskUuid, isWaiting, toast, invalidate],
  )

  return {
    printNextBox,
    printBoxKa,
    printPalletKa,
    isPrinting: isWaiting,
  }
}
